/**
 * React Query hooks for AI Features
 *
 * Production-grade hooks with:
 * - Streaming support for assistant responses
 * - Batch job polling
 * - Cached suggestions
 */

import { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth';
import { AIService } from '../services';
import { applicantKeys } from './useApplicants';

export const aiKeys = {
  all: ['ai'],
  riskSummaries: () => [...aiKeys.all, 'risk-summary'],
  riskSummary: (applicantId) => [...aiKeys.riskSummaries(), applicantId],
  assistant: () => [...aiKeys.all, 'assistant'],
  batchJobs: () => [...aiKeys.all, 'batch'],
  batchJob: (jobId) => [...aiKeys.batchJobs(), jobId],
  documentSuggestions: (documentId) => [...aiKeys.all, 'document-suggestions', documentId],
  companyRiskNarratives: () => [...aiKeys.all, 'company-risk-narrative'],
  companyRiskNarrative: (companyId) => [...aiKeys.companyRiskNarratives(), companyId],
  reviewRecipes: () => [...aiKeys.all, 'review-recipes'],
};

function useAIService() {
  const { getToken } = useAuth();
  return useMemo(() => new AIService(getToken), [getToken]);
}

export function useRiskSummary(applicantId, options = {}) {
  const service = useAIService();

  return useQuery({
    queryKey: aiKeys.riskSummary(applicantId),
    queryFn: ({ signal }) => service.getRiskSummary(applicantId, { signal }),
    enabled: !!applicantId,
    staleTime: 120000, // Risk summaries are stable for 2 minutes
    ...options,
  });
}

export function useRegenerateRiskSummary() {
  const service = useAIService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicantId) => service.regenerateRiskSummary(applicantId),

    // Optimistic update - show loading state
    onMutate: async (applicantId) => {
      await queryClient.cancelQueries({ queryKey: aiKeys.riskSummary(applicantId) });

      const previousSummary = queryClient.getQueryData(aiKeys.riskSummary(applicantId));

      queryClient.setQueryData(aiKeys.riskSummary(applicantId), {
        ...previousSummary,
        status: 'generating',
        generated_at: null,
      });

      return { previousSummary };
    },

    onError: (err, applicantId, context) => {
      if (context?.previousSummary) {
        queryClient.setQueryData(aiKeys.riskSummary(applicantId), context.previousSummary);
      }
    },

    onSuccess: (data, applicantId) => {
      queryClient.setQueryData(aiKeys.riskSummary(applicantId), data);
      queryClient.invalidateQueries({ queryKey: applicantKeys.detail(applicantId) });
    },
  });
}

/**
 * Hook to run the AI Analyst agent's first-level KYC review.
 * Recommend-only: returns a risk level + per-check breakdown + recommendation.
 * Computed on demand (no server cache), so this is a mutation.
 */
export function useAnalystReview() {
  const service = useAIService();
  const queryClient = useQueryClient();

  return useMutation({
    // Accepts an applicant id, or { applicantId, recipe } to run a specific recipe.
    mutationFn: (arg) => {
      const applicantId = typeof arg === 'string' ? arg : arg?.applicantId;
      const recipe = typeof arg === 'string' ? null : arg?.recipe ?? null;
      return service.runAnalystReview(applicantId, recipe);
    },
    onSuccess: (_data, arg) => {
      const applicantId = typeof arg === 'string' ? arg : arg?.applicantId;
      // The agent may have updated the risk score, added a note, and approved.
      queryClient.invalidateQueries({ queryKey: applicantKeys.all });
      queryClient.invalidateQueries({ queryKey: aiKeys.riskSummary(applicantId) });
    },
  });
}

/**
 * Hook to list the tenant's review recipes (named, reusable review definitions)
 * plus the catalog of available checklist sections.
 */
export function useReviewRecipes(options = {}) {
  const service = useAIService();
  return useQuery({
    queryKey: aiKeys.reviewRecipes(),
    queryFn: ({ signal }) => service.listReviewRecipes({ signal }),
    staleTime: 60000,
    ...options,
  });
}

/**
 * Hook to create a new review recipe.
 */
export function useCreateReviewRecipe() {
  const service = useAIService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => service.createReviewRecipe(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiKeys.reviewRecipes() });
    },
  });
}

/**
 * Hook to run the AI Analyst agent across all outstanding applicants.
 * Powers the agent workspace ("review outstanding applicants").
 */
export function useReviewOutstanding() {
  const service = useAIService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (limit = 10) => service.reviewOutstanding(limit),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicantKeys.all });
    },
  });
}

/**
 * Hook to chat with the conversational agent ("Megan").
 * Pass the full message history; returns her reply. Invalidates applicant
 * caches since she may have reviewed/approved applicants.
 */
export function useMeganChat() {
  const service = useAIService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messages) => service.chatWithAgent(messages),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicantKeys.all });
    },
  });
}

/**
 * Hook for the agent's "investigate adverse media" task.
 */
export function useInvestigateAdverseMedia() {
  const service = useAIService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicantId) => service.investigateAdverseMedia(applicantId),
    onSuccess: (_data, applicantId) => {
      queryClient.invalidateQueries({ queryKey: applicantKeys.detail(applicantId) });
    },
  });
}

export function useAskAssistant() {
  const service = useAIService();

  return useMutation({
    mutationFn: ({ query, applicantId, context }) =>
      service.askAssistant(query, applicantId, context),
  });
}

export function useBatchAnalyze() {
  const service = useAIService();

  return useMutation({
    mutationFn: (applicantIds) => service.batchAnalyze(applicantIds),
    // Job started - caller should use useBatchJobStatus to poll for completion
  });
}

export function useBatchJobStatus(jobId, options = {}) {
  const service = useAIService();
  const queryClient = useQueryClient();
  const { onComplete } = options;

  return useQuery({
    queryKey: aiKeys.batchJob(jobId),
    queryFn: ({ signal }) => service.getBatchStatus(jobId, { signal }),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;

      if (data.status === 'completed' || data.status === 'failed') {
        if (onComplete) onComplete(data);
        // Invalidate affected applicant summaries
        if (data.applicant_ids) {
          data.applicant_ids.forEach((id) => {
            queryClient.invalidateQueries({ queryKey: aiKeys.riskSummary(id) });
          });
        }
        return false;
      }
      return 2000;
    },
    staleTime: 0,
    ...options,
  });
}

export function useDocumentSuggestions(documentId, options = {}) {
  const service = useAIService();

  return useQuery({
    queryKey: aiKeys.documentSuggestions(documentId),
    queryFn: ({ signal }) => service.getDocumentSuggestions(documentId, { signal }),
    enabled: !!documentId,
    staleTime: 300000, // Suggestions stable for 5 minutes
    ...options,
  });
}

/**
 * Hook for fetching company risk narrative (KYB)
 * Returns cached narrative from server - only regenerates via POST
 */
export function useCompanyRiskNarrative(companyId, options = {}) {
  const service = useAIService();

  return useQuery({
    queryKey: aiKeys.companyRiskNarrative(companyId),
    queryFn: ({ signal }) => service.getCompanyRiskNarrative(companyId, { signal }),
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes - same as applicant pattern
    ...options,
  });
}

/**
 * Hook for regenerating company risk narrative (uses AI credits)
 */
export function useRegenerateCompanyRiskNarrative() {
  const queryClient = useQueryClient();
  const service = useAIService();

  return useMutation({
    mutationFn: (companyId) => service.regenerateCompanyRiskNarrative(companyId),

    // Optimistic update - show loading state
    onMutate: async (companyId) => {
      await queryClient.cancelQueries({ queryKey: aiKeys.companyRiskNarrative(companyId) });

      const previousNarrative = queryClient.getQueryData(aiKeys.companyRiskNarrative(companyId));

      queryClient.setQueryData(aiKeys.companyRiskNarrative(companyId), {
        ...previousNarrative,
        status: 'generating',
        generated_at: null,
      });

      return { previousNarrative };
    },

    onError: (err, companyId, context) => {
      if (context?.previousNarrative) {
        queryClient.setQueryData(aiKeys.companyRiskNarrative(companyId), context.previousNarrative);
      }
    },

    onSuccess: (data, companyId) => {
      queryClient.setQueryData(aiKeys.companyRiskNarrative(companyId), data);
    },
  });
}

/**
 * Hook for managing an AI assistant conversation
 * Tracks message history and handles sending/receiving
 */
export function useAssistantConversation(applicantId = null) {
  const askAssistant = useAskAssistant();
  const [messages, setMessages] = useState([]);

  const sendMessage = useCallback(
    async (content, context = null) => {
      // Add user message
      const userMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        const response = await askAssistant.mutateAsync({
          query: content,
          applicantId,
          context,
        });

        // Add assistant response
        const assistantMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: response.response,
          sources: response.sources,
          timestamp: response.generated_at || new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        return response;
      } catch (error) {
        // Add error message
        const errorMessage = {
          id: `error-${Date.now()}`,
          role: 'error',
          content: error.message || 'Failed to get response',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        throw error;
      }
    },
    [askAssistant, applicantId]
  );

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const removeMessage = useCallback((messageId) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
  }, []);

  return {
    messages,
    sendMessage,
    clearMessages,
    removeMessage,
    isLoading: askAssistant.isPending,
    error: askAssistant.error,
  };
}

/**
 * Hook for prefetching risk summaries (for list views)
 */
export function usePrefetchRiskSummary() {
  const queryClient = useQueryClient();
  const service = useAIService();

  return useCallback(
    (applicantId) => {
      if (!applicantId) return;
      queryClient.prefetchQuery({
        queryKey: aiKeys.riskSummary(applicantId),
        queryFn: ({ signal }) => service.getRiskSummary(applicantId, { signal }),
        staleTime: 120000,
      });
    },
    [queryClient, service]
  );
}
